import { AUTH_ERROR_CONNECTING, JobStatusEnum, JobTypeEnum } from '@/utils/constants/common';

export const makeAnchor = (str) => {
  return encodeURIComponent(str).replace(/%20/g, '_').replace(/%2F/, '/');
};

export const makeLinkToAnchorSettings = (str) => {
  return `/settings/${str}`;
};

export const makeLinkToAnchorIntegrationSettings = (str, id) => {
  return `/integration/${id}/settings/${str}`;
};

export const makeLinkToAnchor = (str) => {
  return `/docs/api/${str}`;
};

export const makeLinkToAnchorCICD = (str) => {
  return `/docs/ci-cd/${str}`;
};

export const makeLinkToAnchorScanConfigurations = (str) => {
  return `/docs/configurations/${str}`;
};

export const makeSubLinkToAnchorScanConfigurations = (str, path) => {
  return `/docs/${makeAnchor(path)}#${makeAnchor(str)}`;
};

export const makeLinkToAnchorQuickStart = () => {
  return `/docs/quick-start`;
};

export const makeLinkToAnchorWebhooks = () => {
  return `/docs/webhooks`;
};

export const makeSubLinkToAnchor = (str, path) => {
  return `/docs/api/${makeAnchor(path)}#${makeAnchor(str)}`;
};

export const validateEmail = (email) => {
  const re =
    /^(([^<>()[\]\\.,;:\s@"+]+(\.[^<>()[\]\\.,;:\s@"+]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(String(email).toLowerCase());
};

export const isGmail = (email) => {
  return email.includes('gmail.com');
};
export const validateOtp = (otp) => {
  const re = /^\d{6}$/;
  return re.test(otp);
};

export const toggleShowPassword = () => {
  const password = document.getElementById('password');
  const labelShow = document.getElementById('passwordShow');
  const labelHide = document.getElementById('passwordHide');

  if (password.type === 'password') {
    labelShow.style.display = 'none';
    labelHide.style.display = 'block';
    password.type = 'text';
  } else {
    labelShow.style.display = 'block';
    labelHide.style.display = 'none';
    password.type = 'password';
  }
};

// Auth backend machine-readable codes → UI message. Key 'link' is used to redirect to confirm email screen.
const ACCOUNT_BLOCKED_BODY =
  'Unfortunately, your account is out of the trial version or was blocked. Please buy a subscription or contact sales team to renew it';
const SOMETHING_WENT_WRONG = 'Something went wrong';

const ERROR_KEY_CREDENTIALS = 'credentials';
const ERROR_KEY_OTP = 'otp';
const ERROR_KEY_LINK = 'link';
const ERROR_KEY_EMAIL = 'email';
const ERROR_KEY_TIMEOUT = 'timeout';
const ERROR_KEY_VERIFY_EMAIL = 'verifyEmail';
const ERROR_KEY_INVITE_EMAIL_STATUS = 'inviteEmailStatusError';
const ERROR_KEY_COMMON_WEBHOOK = 'commonWebhookError';
const ERROR_KEY_DELETE_REPORT_STATUS = 'deleteReportStatusError';
const ERROR_KEY_DEFAULT = 'default';
const ERROR_KEY_INTERNAL = 'internal';
const ERROR_KEY_FOLDER_NAME = 'folderName';

const MSG_WRONG_CREDENTIALS = 'Wrong credentials, please try again';
const MSG_OTP_REQUIRED = 'OTP required';
const MSG_OTP_INVALID = 'OTP is not valid.';
const MSG_VERIFY_EMAIL = 'Please verify your email address to continue';
const MSG_TOO_MANY_ATTEMPTS = 'Too many attempts. Please try again later.';
const MSG_VALIDATION_FAILED = 'Validation failed';
const MSG_USER_EXISTS_EMAIL = 'User with this email already exists';
const MSG_REQUIRED_FIELD = 'Required field';
const MSG_EMAIL_INVALID = 'Email is not valid';
const MSG_REDIRECT_TO_CONFIRM = 'Redirect to confirm';
const MSG_EMAIL_ALREADY_VERIFIED = 'Email already verified';
const MSG_USE_DIFFERENT_EMAIL = 'Please use a different email';
const MSG_USE_CORPORATE_EMAIL = 'Please use your corporate email';
const MSG_TOO_MANY_REQUESTS = 'Too many requests';
const MSG_SERVER_ERROR = 'Something went wrong, please contact us for details';
const MSG_TEMPORARILY_BLOCKED = 'Your account has been blocked';
const MSG_INVITED_ALREADY = 'The user has already been invited';
const MSG_ALREADY_MEMBER = 'The user is already a member';
const MSG_USER_NOT_FOUND = 'User not found';
const MSG_CANT_INVITE_HACKER = "Can't invite users with the hacker account type";
const MSG_PLEASE_USE_CORP_INVITE = 'Access denied. Please use corporate email to continue';
const MSG_CANNOT_INVITE_SELF = 'You cannot invite yourself to the Integration';
const MSG_URL_INVALID = 'Url is not valid (use https only)';
const MSG_LOCAL_NETWORK = 'IP/domains from local network are not allowed';
const MSG_HTTP_REDIRECTS = 'HTTP redirects are not allowed';
const MSG_VALID_NUMBER = 'Please write a valid number';
const MSG_SERVER_UNAVAILABLE = 'Server is unavailable. Please try again later.';
const MSG_FOLDER_UPDATE_EMPTY_NAME = "Can't update folder with empty name";
const MSG_FOLDER_CREATE_EMPTY_NAME = "Can't create folder with empty name";
const MSG_FOLDER_CREATE_DEFAULT_NAME = "Can't create with Default folder name";
const MSG_FOLDER_NAME_EXISTS = 'Folder with this name already exists';
const MSG_FOLDER_UPDATE_DEFAULT_NAME = "Can't update to Default folder name";
const MSG_PLEASE_USE_SSO = "Sign in using your company's login (SSO)";
const MSG_SSO_ONLY_DOMAIN =
  "This domain uses organization login. Use your company's sign-in instead (SSO).";

export const ERROR_CODES = {
  INVALID_CREDENTIALS: {
    key: ERROR_KEY_CREDENTIALS,
    body: MSG_WRONG_CREDENTIALS,
  },
  OTP_REQUIRED: { key: ERROR_KEY_OTP, body: MSG_OTP_REQUIRED },
  OTP_INVALID: { key: ERROR_KEY_OTP, body: MSG_OTP_INVALID },
  EMAIL_NOT_VERIFIED: { key: ERROR_KEY_LINK, body: MSG_VERIFY_EMAIL },
  ACCOUNT_BLOCKED: { key: ERROR_KEY_CREDENTIALS, body: ACCOUNT_BLOCKED_BODY },
  TEMPORARILY_BLOCKED: {
    key: ERROR_KEY_CREDENTIALS,
    body: MSG_TOO_MANY_ATTEMPTS,
  },
  VALIDATION_FAILED: {
    key: ERROR_KEY_CREDENTIALS,
    body: MSG_VALIDATION_FAILED,
  },
  USER_EXISTS_USERNAME: { key: ERROR_KEY_EMAIL, body: MSG_USER_EXISTS_EMAIL },
  USER_EXISTS_EMAIL: { key: ERROR_KEY_EMAIL, body: MSG_USER_EXISTS_EMAIL },
  USER_NAME_MISSING: { key: ERROR_KEY_EMAIL, body: MSG_REQUIRED_FIELD },
  UNAUTHORIZED: { key: ERROR_KEY_CREDENTIALS, body: MSG_WRONG_CREDENTIALS },
  SOMETHING_WENT_WRONG: {
    key: ERROR_KEY_CREDENTIALS,
    body: SOMETHING_WENT_WRONG,
  },
  EXECUTE_ACTIONS_EMAIL_FAILED: {
    key: ERROR_KEY_EMAIL,
    body: MSG_EMAIL_INVALID,
  },
  REDIRECT_TO_CONFIRM: { key: ERROR_KEY_LINK, body: MSG_REDIRECT_TO_CONFIRM },
  EMAIL_ALREADY_VERIFIED: {
    key: ERROR_KEY_VERIFY_EMAIL,
    body: MSG_EMAIL_ALREADY_VERIFIED,
  },
  USE_DIFFERENT_EMAIL: {
    key: ERROR_KEY_EMAIL,
    body: MSG_USE_DIFFERENT_EMAIL,
  },
  USE_CORPORATE_EMAIL: {
    key: ERROR_KEY_EMAIL,
    body: MSG_USE_CORPORATE_EMAIL,
  },
  TOO_MANY_REQUESTS: { key: ERROR_KEY_TIMEOUT, body: MSG_TOO_MANY_REQUESTS },
  SERVER_ERROR: { key: AUTH_ERROR_CONNECTING, body: MSG_SERVER_ERROR },
  SSO_ONLY: {
    key: ERROR_KEY_CREDENTIALS,
    body: MSG_PLEASE_USE_SSO,
  },
  IDP_LINK_REQUIRED: {
    key: ERROR_KEY_CREDENTIALS,
    body: MSG_PLEASE_USE_SSO,
  },
  SSO_ONLY_DOMAIN: {
    key: ERROR_KEY_EMAIL,
    body: MSG_SSO_ONLY_DOMAIN,
  },
  INVITED_ALREADY: {
    key: ERROR_KEY_INVITE_EMAIL_STATUS,
    body: MSG_INVITED_ALREADY,
  },
  ALREADY_MEMBER: {
    key: ERROR_KEY_INVITE_EMAIL_STATUS,
    body: MSG_ALREADY_MEMBER,
  },
  USER_NOT_FOUND: {
    key: ERROR_KEY_INVITE_EMAIL_STATUS,
    body: MSG_USER_NOT_FOUND,
  },
  CANT_INVITE_HACKER: {
    key: ERROR_KEY_INVITE_EMAIL_STATUS,
    body: MSG_CANT_INVITE_HACKER,
  },
  PLEASE_USE_CORPORATE_EMAIL: {
    key: ERROR_KEY_INVITE_EMAIL_STATUS,
    body: MSG_PLEASE_USE_CORP_INVITE,
  },
  CANNOT_INVITE_SELF: {
    key: ERROR_KEY_INVITE_EMAIL_STATUS,
    body: MSG_CANNOT_INVITE_SELF,
  },
  URL_INVALID: { key: ERROR_KEY_COMMON_WEBHOOK, body: MSG_URL_INVALID },
  LOCAL_NETWORK: { key: ERROR_KEY_COMMON_WEBHOOK, body: MSG_LOCAL_NETWORK },
  HTTP_REDIRECTS: { key: ERROR_KEY_COMMON_WEBHOOK, body: MSG_HTTP_REDIRECTS },
  VALID_NUMBER: {
    key: ERROR_KEY_DELETE_REPORT_STATUS,
    body: MSG_VALID_NUMBER,
  },
  SERVER_UNAVAILABLE: {
    key: ERROR_KEY_INTERNAL,
    body: MSG_SERVER_UNAVAILABLE,
  },
  FOLDER_UPDATE_EMPTY_NAME: {
    key: ERROR_KEY_FOLDER_NAME,
    body: MSG_FOLDER_UPDATE_EMPTY_NAME,
  },
  FOLDER_CREATE_EMPTY_NAME: {
    key: ERROR_KEY_FOLDER_NAME,
    body: MSG_FOLDER_CREATE_EMPTY_NAME,
  },
  FOLDER_CREATE_DEFAULT_NAME: {
    key: ERROR_KEY_FOLDER_NAME,
    body: MSG_FOLDER_CREATE_DEFAULT_NAME,
  },
  FOLDER_NAME_EXISTS: {
    key: ERROR_KEY_FOLDER_NAME,
    body: MSG_FOLDER_NAME_EXISTS,
  },
  FOLDER_UPDATE_DEFAULT_NAME: {
    key: ERROR_KEY_FOLDER_NAME,
    body: MSG_FOLDER_UPDATE_DEFAULT_NAME,
  },
};

const DEFAULT_ERROR = { key: ERROR_KEY_DEFAULT, body: SOMETHING_WENT_WRONG };
const getErrorByBody = (body) => {
  if (!body) {
    return null;
  }

  const bodyText = String(body);
  return Object.values(ERROR_CODES).find((error) => error.body === bodyText) || null;
};

/**
 * @param {string|{code?: string}|null|undefined} error
 * @returns {{key: string, body: string}}
 */
export const convertErrorMessageForUI = (error) => {
  if (error == null || error === undefined) {
    return DEFAULT_ERROR;
  }

  if (typeof error === 'string') {
    return ERROR_CODES[error] || getErrorByBody(error) || DEFAULT_ERROR;
  }

  const isObject = typeof error === 'object';
  const code = isObject ? error?.code : null;

  const mappedError = code ? ERROR_CODES[code] || getErrorByBody(code) : null;

  return mappedError || DEFAULT_ERROR;
};

export const setItemsToLocalStorage = (items) => {
  items.forEach((item) => {
    const [key, value] = item;

    localStorage.setItem(key, value);
  });
};

export const deleteItemsFromLocalStorage = (items) => {
  items.forEach((item) => {
    localStorage.removeItem(item);
  });
};

export const checkFormInputsForErrors = (fields, inputErrorClass) => {
  Object.keys(fields).forEach((field) => {
    if (!fields[field]) {
      raiseError(field, `${field}Error`, inputErrorClass);
    }
  });
};

export const checkFieldForError = (field, fieldValue, inputErrorClass) => {
  if (!fieldValue) {
    raiseError(field, `${field}Error`, inputErrorClass);
  }
};

export const checkFormInputsLength = (fields, inputErrorClass) => {
  Object.keys(fields).forEach((field) => {
    if (field && field.length > 1000) raiseError(field, `${field}LengthError`, inputErrorClass);
  });
};

export const validatePassword = (password) => password.length >= 12;
export const checkPasswordLength = (fields, inputErrorClass) => {
  Object.keys(fields).forEach((field) => {
    if (!validatePassword(fields[field])) {
      raiseError(field, `${field}LengthError`, inputErrorClass);
    }
  });
};

export const checkEmailValidation = (emailField, inputErrorClass) => {
  Object.keys(emailField).forEach((field) => {
    if (!validateEmail(emailField[field])) {
      raiseError(field, `${field}ValidationError`, inputErrorClass);
    }
  });
};

export const isEmpty = (object) => {
  for (const property in object) {
    return false;
  }
  return true;
};

export const checkFormInputsForCompleteness = (fields) => {
  return Object.keys(fields)
    .map((field) => {
      return !!fields[field] && fields[field].length < 1000;
    })
    .every((el) => el);
};

export const checkFieldCompleteness = (value, maxLength = 1000) => {
  return value.length < maxLength;
};

export const raiseError = (inputId, errorId, inputErrorClass) => {
  const element = document.getElementById(inputId);
  if (!element) return;
  element.classList.add(inputErrorClass);
  document.getElementById(errorId).style.display = 'block';
  document.getElementById(inputId).style.border = '1px solid #FB8D62';
  element.addEventListener('click', () => {
    element.classList.remove(inputErrorClass);
    document.getElementById(errorId).style.display = 'none';
    document.getElementById(inputId).style.border = '1px solid #F0F0F0';
  });
};
// todo This code is decoding if data was previously encoded
// export const decodeFromBase64 = (str) => {
//     const decoded = CryptoJS.enc.Base64.parse(str);
//     return CryptoJS.enc.Utf8.stringify(decoded);
// }

// export const handleDecodeSignatureToObj = (signature) => {
//     return JSON.parse(decodeFromBase64(signature))
// }

export const isNewDevLocation = () => window.location.host === 'new.oversecured.com';
export const isNewProdLocation = () => window.location.host === 'hasdgkjwewer.oversecured.com';
export const isProdLocation = () => window.location.host === 'oversecured.com';

export const validateString = (value) => {
  if (value && typeof value === 'string') {
    return value;
  } else {
    return '';
  }
};

export const calculateDateForSettings = (date) => {
  const versionDate = Date.parse(date);
  const currentDate = Date.now();
  const oneDayInUnix = 24 * 60 * 60 * 1000;
  if (versionDate + oneDayInUnix > currentDate) {
    const timeAfterVersionFinished = currentDate - versionDate;
    const timeInHours = timeAfterVersionFinished / 1000 / 60 / 60;
    if (timeInHours >= 1) {
      return `${Math.floor(timeInHours)} h ago`;
    } else {
      const timeInMinutes = timeInHours * 60;
      return `${Math.floor(timeInMinutes)} min ago`;
    }
  } else {
    const changeFormatDate = new Date(date);
    const currentFormatDate = changeFormatDate.toDateString().split(' ');
    return `${currentFormatDate[1]}  ${currentFormatDate[2]}, ${currentFormatDate[3]}`;
  }
};

export const isArrayNotEmpty = (arr) => {
  return Array.isArray(arr) && arr.length > 0;
};

export const checkFilesAndUpdatePrice = (files, id) => {
  return files && files[id] ? files[id].price : 0;
};

export const convertCentsToDollars = (cents) => {
  if (typeof cents === 'number') {
    return cents / 100;
  } else {
    return 0;
  }
};

export const validateUuid = (id) => {
  return (
    validateString(id) &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(id)
  );
};

export const compareFn = (a, b) => (a.position < b.position ? 1 : b.position < a.position ? -1 : 0);

export const convertCategoryPositionToNumber = (categoryList) => {
  if (categoryList.length) {
    return categoryList.map((category) => {
      category.position = Number.parseInt(category.position);
      return category;
    });
  } else {
    return [];
  }
};
export const checkAndUpdatePlatformText = (platform) => {
  if (platform === 'android') {
    return 'Android';
  } else if (platform === 'ios') {
    return 'iOS';
  } else {
    return '-';
  }
};

export const hideErrors = (errorIdsArr) => {
  if (isArrayNotEmpty(errorIdsArr)) {
    errorIdsArr.forEach((error) => {
      const errorElement = document.getElementById(error);
      if (errorElement) {
        errorElement.style.display = 'none';
      }
    });
  }
};

export const checkInputForErrors = (errorId) => {
  return (
    document.getElementById(errorId) && document.getElementById(errorId).style.display === 'none'
  );
};

export const isNotEmptyString = (string) => {
  return string && typeof string === 'string' && string.trim().length > 0;
};

export const isUrlValid = (url) => {
  let path;
  try {
    path = new URL(url);
  } catch (_) {
    return false;
  }
  return isNotEmptyString(url) && path.origin !== 'null';
};

export const getVersionStatus = (status) => {
  switch (status) {
    case JobStatusEnum.CREATED:
      return 'Created';
    case JobStatusEnum.IN_QUEUE:
      return 'In queue';
    case JobStatusEnum.PROCESSING:
      return 'Processing';
    case JobStatusEnum.SCANNING:
      return 'Scanning...';
    case JobStatusEnum.ERROR:
      return 'Failed';
    case JobStatusEnum.FINISHED:
      return 'Finished';
    default:
      return '-';
  }
};

export const sortByField = (field) => {
  return (a, b) => (a[field] < b[field] ? 1 : -1);
};

export const createDateView = (date) => {
  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  if (isLessThenOneMinuteTime(date)) return 'new';
  if (isLessThenOneHourTime(date)) {
    // '8 min ago'
    let milliseconds = Date.now() - Date.parse(date);
    return `${(milliseconds / (60 * 1000)).toFixed()} min ago`;
  }
  if (isLessThenOneDayTime(date)) {
    // '23 h ago'
    let milliseconds = Date.now() - Date.parse(date);
    return `${(milliseconds / (60 * 60 * 1000)).toFixed()} h ago`;
  }
  // '24 Jan, 2020'
  let parsedDate = new Date(Date.parse(date));
  return `${months[parsedDate.getMonth()]} ${parsedDate.getDate()}, ${parsedDate.getFullYear()}`;
};

export const isLessThenOneMinuteTime = (date) => {
  return date ? Date.now() - Date.parse(date) < 60 * 1000 : true;
};

const isLessThenOneHourTime = (date) => {
  return Date.now() - Date.parse(date) < 60 * 60 * 1000;
};

const isLessThenOneDayTime = (date) => {
  return Date.now() - Date.parse(date) < 24 * 60 * 60 * 1000;
};

export const isDisplayScanBtn = (isJobOwner, jobType) => {
  return (
    jobType === JobTypeEnum.INTEGRATION || jobType === JobTypeEnum.VERSION_SAMPLE || isJobOwner
  );
};

export const shouldDisplay = (isJobOwner, jobType) => {
  return (
    jobType === JobTypeEnum.INTEGRATION || jobType === JobTypeEnum.VERSION_SAMPLE || isJobOwner
  );
};

// isHighSeverityExist and isMediumSeverityExist for ReportStatistic outdated and supposed to be removed isSeverityExist - new
export const isHighSeverityExist = (statistics) => {
  return statistics.percentageBySeverity.high > 0 ? true : false;
};

export const isMediumSeverityExist = (statistics) => {
  return statistics.percentageBySeverity.medium > 0 ? true : false;
};

export const isSeverityExist = (value) => {
  return value > 0 ? true : false;
};

export const checkIfMobile = (window) => {
  // This is a simple regex for mobile detection, it checks for the most common mobile device indicators in the user agent string.
  const mobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
  return mobileRegex.test(window.navigator.userAgent);
};

/**
 * Set multiple CSS styles to a DOM element.
 *
 * @param {Element} element - The DOM element to apply styles to.
 * @param {Object} styles - An object containing CSS properties and their values.
 */
const setStyle = (element, styles) => {
  if (!element) return;
  Object.assign(element.style, styles);
};

/**
 * Adjusts the width of various elements based on the provided width value.
 * Also handles transition effects for smoother visual adjustments.
 *
 * @param {number} width - The width value (typically the window's width).
 * @param {string} page - The name of the current page.
 */
export const adjustWidths = (width, page) => {
  if (!width) return;

  const container = document.querySelector('.page-content');
  const mainReport = document.querySelector('.main-report-container');
  const reportPage = document.querySelector('.report-page');
  const codeWrapper = document.querySelector('.code-wrapper');

  /**
   * Sets transition styles to key elements.
   */
  const setTransition = () => {
    const transitionStyle = '0.2s ease-in-out';
    setStyle(container, { transition: `max-width ${transitionStyle}` });
    setStyle(mainReport, { transition: `width ${transitionStyle}` });
    setStyle(reportPage, { transition: `all ${transitionStyle}` });
  };

  /**
   * Adjusts the width styles of key elements based on the current screen width.
   */
  const setWidthStyles = () => {
    if (page !== 'ScanInfo' && page !== 'Version scan' && page !== 'GuestScanInfo') return;

    if (width <= 576) {
      // do nothing
      // not refactoring this since the logic is going to be thrown away with the new design implementation
    } else if (width <= 834) {
      setStyle(container, { 'max-width': `${width - 20}px` });
      setStyle(mainReport, { width: `${width - 60}px` });
      setStyle(reportPage, { width: `${width - 232}px` });
      setStyle(codeWrapper, { width: `${width - 232}px` });
    } else {
      setStyle(container, { 'max-width': `${width}px` });
      setStyle(mainReport, { width: `${width}px` });
      setStyle(reportPage, { width: `${width - 232}px` });
      setStyle(codeWrapper, { width: `${width - 232}px` });
    }
  };

  setTransition();
  setWidthStyles();
};

/**
 * Adjusts the widths of header and footer elements based on the provided width value.
 * Also handles transition effects for smoother visual adjustments.
 *
 * @param {number} width - The width value (typically the window's width).
 * @param {string} page - The name of the current page.
 */
export const adjustHeaderAndFooterWidths = (width, page) => {
  if (!width) return;

  const header = document.querySelector('.header');
  const footer = document.querySelector('#footer');

  /**
   * Sets basic styles to header and footer elements.
   */
  const setBasicStyles = () => {
    if (width <= 576) {
      setStyle(header, {
        'min-width': 'unset',
        'max-width': `${width}px`,
        transition: 'max-width 0.2s ease-in-out',
      });
      setStyle(footer, {
        'min-width': 'unset',
        width: `${width - 40}px`,
        transition: 'max-width 0.2s ease-in-out',
      });
    } else {
      setStyle(header, {
        'min-width': 'unset',
        'max-width': `${width - width * 0.15}px`,
        transition: 'max-width 0.2s ease-in-out',
      });
      setStyle(footer, {
        'min-width': 'unset',
        width: `${width - width * 0.15}px`,
        transition: 'max-width 0.2s ease-in-out',
      });
    }
  };

  /**
   * Adjusts the widths of header and footer elements based on the type of page and screen width.
   */
  const adjustForPageType = () => {
    if (page !== 'ScanInfo' && page !== 'Version scan') return;

    if (width <= 576) {
      setStyle(header, { 'max-width': `${width}px` });
      setStyle(footer, { width: `${width - 40}px` });
    } else if (width <= 834) {
      setStyle(footer, { width: `${width - 232}px` });
    } else {
      setStyle(footer, { width: `${width - 232}px` });
    }
  };

  setBasicStyles();
  adjustForPageType();
};

export const calculateTotalStatisticsCount = (folder) => {
  if (folder && folder.statistics) {
    return folder.statistics.reduce((total, stat) => total + stat.count, 0);
  }
  return 0;
};

export const transformVulnerabilitiesIds = (originalData) => {
  const transformed = {};
  Object.keys(originalData).forEach((key) => {
    transformed[key] = originalData[key].map((obj) => obj.categoryId);
  });
  return transformed;
};
